import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

type Filter = '7d' | '30d' | 'all';

interface Props { compact?: boolean; }

export function GraphPanel({ compact }: Props) {
  const sessions = useSelector((s: RootState) => s.sessions.list);
  const [filter, setFilter] = useState<Filter>('all');

  const now = new Date();
  const filtered = sessions
    .filter(s => {
      if (filter === 'all') return true;
      const days = filter === '7d' ? 7 : 30;
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - days);
      return new Date(s.date) >= cutoff;
    })
    .slice().sort((a, b) => a.date.localeCompare(b.date));

  let cumulative = 0;
  const chartData = filtered.map(s => {
    cumulative += s.netProfit;
    return { date: s.date.slice(5), profit: parseFloat(cumulative.toFixed(2)), session: parseFloat(s.netProfit.toFixed(2)) };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const sess = payload.find((p: any) => p.dataKey === 'session');
    const cum = payload.find((p: any) => p.dataKey === 'profit');
    return (
      <div className="graph-tooltip">
        <div className="gt-date">{label}</div>
        {sess && <div className={sess.value >= 0 ? 'pos' : 'neg'}>Sesión: {sess.value >= 0 ? '+' : ''}${sess.value}</div>}
        {cum && <div>Acum: ${cum.value}</div>}
      </div>
    );
  };

  return (
    <div className="panel graph-panel">
      <div className="panel-header">
        <span className="panel-title">PROFIT ACUMULADO</span>
        <div className="filter-btns">
          {(['7d', '30d', 'all'] as Filter[]).map(f => (
            <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === '7d' ? '7d' : f === '30d' ? '30d' : 'Todo'}
            </button>
          ))}
        </div>
      </div>
      {chartData.length === 0 ? (
        <div className="graph-empty">Sin datos para mostrar</div>
      ) : (
        <ResponsiveContainer width="100%" height={compact ? 180 : 240}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a3020" />
            <XAxis dataKey="date" stroke="#2a5a38" tick={{ fontSize: 10, fill: '#4a8060', fontFamily: 'Space Mono, monospace' }} />
            <YAxis stroke="#2a5a38" tick={{ fontSize: 10, fill: '#4a8060', fontFamily: 'Space Mono, monospace' }} tickFormatter={v => `$${v}`} width={55} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#2a5a38" strokeDasharray="4 4" />
            <Line type="monotone" dataKey="profit" stroke="#00e676" strokeWidth={2} dot={{ r: 2, fill: '#00e676' }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
